import { useMemo, useState } from "react";
import { ReferenceLine , LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

// Helpers
const toTs = (t) => new Date(t).getTime();
const toMap = (arr) => new Map((arr || []).map((o) => [toTs(o.time), o.fwi]));

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  // Detectar si es tramo de obs o pred
  const hasObs = payload.some(
    (p) =>
      ["torreObs", "bravo", "delta1", "delta2"].includes(p.dataKey) &&
      p.value != null
  );

  const values = {
    vallesa: hasObs
      ? payload.find((p) => p.dataKey === "torreObs")?.value
      : payload.find((p) => p.dataKey === "torrePred")?.value,
    bravo: hasObs
      ? payload.find((p) => p.dataKey === "bravo")?.value
      : payload.find((p) => p.dataKey === "bravoPred")?.value,
    delta1: hasObs
      ? payload.find((p) => p.dataKey === "delta1")?.value
      : payload.find((p) => p.dataKey === "delta1Pred")?.value,
    delta2: hasObs
      ? payload.find((p) => p.dataKey === "delta2")?.value
      : payload.find((p) => p.dataKey === "delta2Pred")?.value,
  };

  return (
    <div style={{ background: "#fff", padding: "8px", border: "1px solid #ccc", fontSize: 12 }}>
      <div><strong>{new Date(label).toLocaleString("es-ES")}</strong></div>
      <div style={{ color: "#007bff" }}>Vallesa: {values.vallesa ?? ""}</div>
      <div style={{ color: "#28a745" }}>Cañada Norte: {values.bravo ?? ""}</div>
      <div style={{ color: "#6f42c1" }}>Cañada Sur 1: {values.delta1 ?? ""}</div>
      <div style={{ color: "#fd7e14" }}>Cañada Sur 2: {values.delta2 ?? ""}</div>
    </div>
  );
};



const GraficaFwi = ({
  torreVallesaObservations,
  torreVallesaPrediction,
  cannonBravo,
  cannonBravoPrediction,
  cannonDelta1,
  cannonDelta1Prediction,
  cannonDelta2,
  cannonDelta2Prediction,
  isLoading,
  isLoadingPredictions
}) => {
  // Map por serie
  const obsMap = useMemo(() => toMap(torreVallesaObservations), [torreVallesaObservations]);
  const predMap = useMemo(() => toMap(torreVallesaPrediction?.predictions || []), [torreVallesaPrediction]);

  const bravoMap = useMemo(() => toMap(cannonBravo), [cannonBravo]);
  const bravoPredMap = useMemo(() => toMap(cannonBravoPrediction?.predictions || []), [cannonBravoPrediction]);

  const delta1Map = useMemo(() => toMap(cannonDelta1), [cannonDelta1]);
  const delta1PredMap = useMemo(() => toMap(cannonDelta1Prediction?.predictions || []), [cannonDelta1Prediction]);

  const delta2Map = useMemo(() => toMap(cannonDelta2), [cannonDelta2]);
  const delta2PredMap = useMemo(() => toMap(cannonDelta2Prediction?.predictions || []), [cannonDelta2Prediction]);

  // Dataset unificado
  const data = useMemo(() => {
    const tsSet = new Set([
      ...obsMap.keys(),
      ...predMap.keys(),
      ...bravoMap.keys(),
      ...bravoPredMap.keys(),
      ...delta1Map.keys(),
      ...delta1PredMap.keys(),
      ...delta2Map.keys(),
      ...delta2PredMap.keys(),
    ]);
    return Array.from(tsSet)
      .sort((a, b) => a - b)
      .map((ts) => ({
        timestamp: ts,
        torreObs: obsMap.get(ts) ?? null,
        torrePred: predMap.get(ts) ?? null,
        bravo: bravoMap.get(ts) ?? null,
        bravoPred: bravoPredMap.get(ts) ?? null,
        delta1: delta1Map.get(ts) ?? null,
        delta1Pred: delta1PredMap.get(ts) ?? null,
        delta2: delta2Map.get(ts) ?? null,
        delta2Pred: delta2PredMap.get(ts) ?? null,
      }));
  }, [obsMap, predMap, bravoMap, bravoPredMap, delta1Map, delta1PredMap, delta2Map, delta2PredMap]);

  // Ticks a las 00:00 y 12:00 de cada día del rango
  const xTicks = useMemo(() => {
    if (!data.length) return [];
    const min = data[0].timestamp;
    const max = data[data.length - 1].timestamp;
    const ticks = [];
    const d = new Date(min);
    d.setMinutes(0, 0, 0);
    if (d.getHours() >= 1) {
      d.setDate(d.getDate() + 1);
      d.setHours(0);
    } else {
      d.setHours(0);
    }
    while (d.getTime() <= max) {
      ticks.push(d.getTime());
      d.setHours(d.getHours() === 0 ? 12 : 24);
    }
    return ticks;
  }, [data]);

  // Estado de visibilidad por grupo
  const [visibleGroups, setVisibleGroups] = useState({
    vallesa: true,
    bravo: true,
    delta1: true,
    delta2: true,
  });

  // Leyenda personalizada agrupada
  const groups = [
    { key: "vallesa", label: "Vallesa", color: "#007bff" },
    { key: "bravo", label: "Cañada Norte", color: "#28a745" },
    { key: "delta1", label: "Cañada Sur 1", color: "#6f42c1" },
    { key: "delta2", label: "Cañada Sur 2", color: "#fd7e14" },
  ];

  const renderLegend = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "6px" }}>
      {groups.map((g) => {
        const active = visibleGroups[g.key];
        return (
          <button
            key={g.key}
            onClick={() => setVisibleGroups((v) => ({ ...v, [g.key]: !v[g.key] }))}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#333",
              opacity: active ? 1 : 0.45,
              textDecoration: active ? "none" : "line-through",
            }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: g.color,
              }}
            />
            <span>{g.label}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div>
      <h3>🔥 FWI</h3>

      {isLoading && data.length === 0 ? (
        <p>Cargando FWI...</p>
      ) : data.length > 0 ? (
        <>
          <LineChart
            width={800}
            height={400}
            data={data}
            margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "28px",
              padding: "10px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              marginTop: "10px",
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={["auto", "auto"]}
              ticks={xTicks}
              tickFormatter={(time) => {
                const d = new Date(time);
                const day = d.toLocaleDateString("es-ES", { weekday: "short" });
                const hour = d.getHours() === 0 ? "00:00" : "12:00";
                return `${day} - ${hour}`;
              }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              label={{ value: "FWI", angle: -90, position: "insideLeft" }} 
              domain={[0, 45]}
            />
            <Tooltip content={<CustomTooltip />} offset={120} />
            <Legend align="right" layout="vertical" verticalAlign="middle" content={renderLegend} />
            
            {/* Líneas de umbrales */}
            <ReferenceLine y={40} stroke="red" strokeDasharray="5 3" strokeWidth={1} />
            <ReferenceLine y={30} stroke="orange" strokeDasharray="5 3" strokeWidth={1} />
            <ReferenceLine y={20} stroke="#f4e700ff" strokeDasharray="5 3" strokeWidth={1} />
            
            {/* Series por grupo */}
            <Line dataKey="torreObs" stroke="#007bff" dot={false} connectNulls isAnimationActive={false} hide={!visibleGroups.vallesa} />
            <Line dataKey="torrePred" stroke="#007bff" strokeDasharray="5 5" dot={false} connectNulls isAnimationActive={false} hide={!visibleGroups.vallesa} />

            <Line dataKey="bravo" stroke="#28a745" dot={false} connectNulls isAnimationActive={false} hide={!visibleGroups.bravo} />
            <Line dataKey="bravoPred" stroke="#28a745" strokeDasharray="5 5" dot={false} connectNulls isAnimationActive={false} hide={!visibleGroups.bravo} />

            <Line dataKey="delta1" stroke="#6f42c1" dot={false} connectNulls isAnimationActive={false} hide={!visibleGroups.delta1} />
            <Line dataKey="delta1Pred" stroke="#6f42c1" strokeDasharray="5 5" dot={false} connectNulls isAnimationActive={false} hide={!visibleGroups.delta1} />

            <Line dataKey="delta2" stroke="#fd7e14" dot={false} connectNulls isAnimationActive={false} hide={!visibleGroups.delta2} />
            <Line dataKey="delta2Pred" stroke="#fd7e14" strokeDasharray="5 5" dot={false} connectNulls isAnimationActive={false} hide={!visibleGroups.delta2} />
          </LineChart>

          {isLoadingPredictions && (
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <div className="spinner" style={{
                width: 18,
                height: 18,
                border: "3px solid #ccc",
                borderTop: "3px solid #007bff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }} />
              <span style={{ fontStyle: "italic" }}>Cargando predicciones...</span>
            </div>
          )}
        </>
      ) : (
        <p>No hay datos disponibles</p>
      )}
    </div>
  );

};

export default GraficaFwi;
